import { Link, useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { EnrollmentStepWrapper } from '../styles/Enrollment.styled'
import { Button } from '../styles/Button.styled'
import { Helmet } from 'react-helmet'
import { PlanProps } from '../../types'
import { useEffect, useState, useRef } from 'react'
import { useCookies } from 'react-cookie'
import moment from 'moment'
import { trackEvent } from '../../analytics'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface Props {
    show: boolean
    result: any
    sign: string
    data?: PlanProps
    provider_id?: string | number
    backPlanListPage?: () => void
}

export const EnrollmentStepConfirmation: React.FC<Props> = ({
    result,
    show,
    sign,
    data,
    provider_id,
    backPlanListPage,
}) => {
    const [ provider, setProvider ] = useState('');
    const [ cookies, setCookie ] = useCookies(['show_subscribe_popup']);
    const hasTrackedOutcome = useRef(false);
    const hasSetSubscribeCookie = useRef(false);

    useEffect(() => {
        if (data && data.source) {
            setProvider(data.source);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!show || !result?.status || hasTrackedOutcome.current) return

        const statusCode = String(result.status).split('.')[0]
        const params = {
            plan_id: data?.plan_id,
            provider_id: data?.provider_id,
            source: data?.source,
            status: statusCode,
        }

        if (statusCode === '50') {
            hasTrackedOutcome.current = true
            trackEvent('enrollment_fail', {
                ...params,
                reason: 'rejected',
            })
            return
        }

        if (['10', '20', '30', '40'].includes(statusCode)) {
            hasTrackedOutcome.current = true
            trackEvent('generate_lead', params)
        }
    }, [show, result, data])

    useEffect(() => {
        if (!show || !result?.status || !data || hasSetSubscribeCookie.current) return

        const statusCode = String(result.status).split('.')[0]
        if (statusCode !== '10') return

        hasSetSubscribeCookie.current = true
        const val = moment()
            .add(data.contract_term ? data.contract_term : 3, 'months')
            .format('YYYY-MM-DD')

        if (cookies.show_subscribe_popup) {
            const res = decodeURIComponent(cookies.show_subscribe_popup).split('|')
            res[0] = val
            setCookie('show_subscribe_popup', res.join('|'), { path: '/', maxAge: 2147483647 })
        } else {
            setCookie('show_subscribe_popup', `${val}||`, { path: '/', maxAge: 2147483647 })
        }
    }, [show, result, data, cookies.show_subscribe_popup, setCookie])

    const showResult = () => {
        if (result) {
            const { additional, message, status } = result
            if (status == null || status === '') return null

            const errorCode = String(status).split('.')[0]
            switch (errorCode) {
                case '10':
                    return <Successful provider={provider} />
                case '20':
                    return <PendingDeposit additional={additional} provider={provider} />
                case '30':
                    return (
                        <PendingVerification
                            message={message}
                            additional={additional}
                            provider={provider}
                            provider_id={provider_id ?? data?.provider_id}
                            backPlanListPage={backPlanListPage}
                        />
                    )
                case '40':
                    return <ContactLater provider={provider} />
                case '50':
                    return <Rejected provider={provider} />
                default:
                    return null
            }
        }
        return null
    }

    if (!show) return null
    return (
        <EnrollmentStepWrapper>
            <Helmet>
                <title> Confirmation Page | TEXAS ENERGY GROUP </title>
                <meta
                    name="description"
                    content="Confirmation page to show if your application is approved, rejected or pending further review by the selected retail energy provider."
                />
            </Helmet>
            <div className="p-4">{showResult()}</div>
        </EnrollmentStepWrapper>
    )
}

const Successful = ({ provider }: { provider: string; }) => {
    const { t } = useTranslation('enrollment')
    const navigate = useNavigate()
    return (
        <div>
            <div className="text-center">
                <h1>{t('Congratulations')}!</h1>
                <p>
                    {t(
                        'Your application has been approved! Please wait for the email confirmation from the Retail Energy Provider (REP) including the plan details and the next steps. Thank you for choosing TEXAS ENERGY GROUP.'
                    )}
                </p>
                {
                    provider && provider === 'constellation' &&
                    <p>{t('Dedicated Toll-Free')}: 1-855-796-0871</p>
                }
                <div className="mt-5">
                    <Button
                        onClick={() => {
                            navigate('/')
                        }}>
                        {t('Close the Window')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

const Rejected = ({ provider }: { provider: string; }) => {
    const { t } = useTranslation('enrollment')
    const navigate = useNavigate()
    return (
        <div>
            <div className="text-center">
                <h1>{t('Your application was not approved')}</h1>
                <Trans
                    i18nKey="enrollment:RejectionDesc"
                    components={[
                        <Link to="/contact" style={{ color: 'var(--brand)' }} />,
                    ]}
                />
                {
                    provider && provider === 'constellation' &&
                    <p>{t('Dedicated Toll-Free')}: 1-855-796-0871</p>
                }
                <div className="mt-5">
                    <Button
                        onClick={() => {
                            navigate('/')
                        }}>
                        {t('Close the Window')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

const PendingDeposit = ({ additional, provider }: {
    additional: any;
    provider: string;
}) => {
    const { t } = useTranslation('enrollment')

    const getAdditionalContent = () => {
        if (!Array.isArray(additional) || additional.length === 0) return null

        return additional.map((val: any, idx: number) => {
            return (
                <div className="mb-2" key={idx}>
                    <Button
                        onClick={() => {
                            const actionValue = val.link ?? val.value
                            if (typeof actionValue === 'string' && actionValue.indexOf('http') === 0) {
                                if (typeof window !== 'undefined') {
                                    window.open(actionValue, '_blank')
                                }
                            } else if (typeof actionValue === 'string') {
                                document.location.href = `tel:${actionValue}`
                            }
                        }}>
                        {t(val.label)}
                    </Button>
                </div>
            )
        })
    }

    return (
        <div>
            <div className="text-center">
                <h1>{t('We are almost there!')}</h1>
                <p>
                    {t(
                        'Thank you for your patience. In order to complete your enrollment, please click the button below to pay your deposit via credit card.'
                    )}
                </p>
                {
                    provider && provider === 'constellation' &&
                    <p>{t('Dedicated Toll-Free')}: 1-855-796-0871</p>
                }
                <div className="mt-5">{getAdditionalContent()}</div>
            </div>
            <div className="mt-5">
                <h5>{t('Why should I pay the deposit?')}</h5>
                <p>
                    {t(
                        'Deposit is required by the Retail Energy Provider after a careful review of your credit history. Your payment information will be sent to the Retail Energy Provider to process your deposit and will not be stored, used or shared by LEB in any means.'
                    )}
                </p>
            </div>
        </div>
    )
}

const PendingVerification = ({
    message,
    additional,
    provider,
    provider_id,
    backPlanListPage,
}: {
    message: string;
    additional: any;
    provider: string;
    provider_id?: string | number;
    backPlanListPage?: () => void;
}) => {
    const { t } = useTranslation('enrollment')
    const navigate = useNavigate()
    const isAtlantex = Number(provider_id) === 57
    const confirmationNumber = additional?.[0]?.confirmation_number

    const getAdditionalContent = () => {
        if (!Array.isArray(additional) || additional.length === 0) return null

        return additional.map((val: any, idx: number) => {
            return (
                <div className="mb-2" key={idx}>
                    <Button
                        onClick={() => {
                            if (val.link.indexOf('http') === 0) {
                                if (typeof window !== 'undefined') {
                                    window.open(val.link, '_blank')
                                }
                            } else {
                                document.location.href = `tel:${val.link}`
                            }
                        }}>
                        {t(val.label)}
                    </Button>
                </div>
            )
        })
    }

    if (isAtlantex) {
        return (
            <div style={{ maxWidth: 750, margin: '0 auto' }}>
                <h1
                    style={{
                        textAlign: 'center',
                        fontWeight: 700,
                        fontSize: '28px',
                        marginBottom: '24px',
                    }}
                >
                    {t('Atlantex Power is requiring ID verification')}
                </h1>

                <div
                    style={{
                        backgroundColor: 'rgba(112, 198, 24, 0.08)',
                        padding: '24px',
                        borderRadius: '12px',
                        border: '1px solid rgba(112, 198, 24, 0.25)',
                    }}
                >
                    <h3
                        style={{
                            marginTop: 0,
                            fontFamily: 'var(--font-heading)',
                            fontSize: '18px',
                            fontWeight: 600,
                            letterSpacing: 'normal',
                        }}
                    >
                        {t(
                            'Atlantex Power requires additional ID verification to complete your enrollment.'
                        )}
                    </h3>

                    <div
                        style={{
                            backgroundColor: '#fafafa',
                            borderRadius: '8px',
                            padding: '16px',
                            margin: '16px 0',
                            border: '1px solid #ddd',
                        }}
                    >
                        <h5 style={{ marginTop: 0, marginBottom: '12px', fontWeight: 600 }}>
                            {t('What you need to do:')}
                        </h5>
                        <p style={{ margin: '6px 0' }}>
                            1.{' '}
                            {t(
                                "Take a clear photo or scan of your driver's license or government-issued ID"
                            )}
                        </p>
                        <p style={{ margin: '6px 0' }}>
                            2. {t('Email it to')}:{' '}
                            <a
                                href="mailto:care@atlantexpower.com"
                                style={{
                                    color: 'var(--brand)',
                                    textDecoration: 'none',
                                    fontWeight: 'bold',
                                }}
                            >
                                care@atlantexpower.com
                            </a>
                        </p>
                        <p style={{ margin: '6px 0' }}>
                            3. {t('Include your Application ID')}:{' '}
                            <strong style={{ color: 'var(--brand)', fontWeight: 'bold' }}>
                                {confirmationNumber}
                            </strong>
                        </p>
                    </div>

                    <div
                        style={{
                            fontSize: '14px',
                            backgroundColor: 'rgba(112, 198, 24, 0.12)',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '10px 16px',
                            borderRadius: '8px',
                        }}
                    >
                        <FontAwesomeIcon
                            icon="info-circle"
                            style={{ marginRight: '8px', color: 'var(--brand)' }}
                        />
                        {t(
                            'You will also receive an email from Atlantex Power with these same instructions.'
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                    <button
                        type="button"
                        style={{
                            backgroundColor: 'var(--brand)',
                            color: 'white',
                            padding: '10px 24px',
                            borderRadius: '8px',
                            border: 'none',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: '16px',
                        }}
                        onClick={() => {
                            if (backPlanListPage) {
                                backPlanListPage()
                            } else {
                                navigate('/')
                            }
                        }}
                    >
                        <FontAwesomeIcon
                            icon="arrow-left"
                            style={{ marginRight: '8px', color: '#fff' }}
                        />{' '}
                        {t('Try another plan')}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div>
            <div className="text-center">
                <h1>{t('Pending verification by the Retail Energy Provider')}</h1>
                <p>
                    {t(
                        'The Retail Energy Provider (REP) is verifying some of your information and it might take a little longer. We will send an email to inform you of the decision once we hear back from the REP.  Hold tight and we are almost there!'
                    )}
                </p>
                <p>{t(message)}</p>
                {
                    provider && provider === 'constellation' &&
                    <p>{t('Dedicated Toll-Free')}: 1-855-796-0871</p>
                }
                <div className="mt-5">
                    {getAdditionalContent()}
                    <Button
                        onClick={() => {
                            navigate('/')
                        }}>
                        {t('Close the Window')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

const ContactLater = ({ provider }: { provider: string; }) => {
    const { t } = useTranslation('enrollment')
    const navigate = useNavigate()
    return (
        <div>
            <div className="text-center">
                <h1>{t('Pending Review')}</h1>
                <p>
                    {t(
                        'Your application was received. The decision will be sent to your email address shortly. Thank you for choosing TEXAS ENERGY GROUP.'
                    )}
                </p>
                {
                    provider && provider === 'constellation' &&
                    <p>{t('Dedicated Toll-Free')}: 1-855-796-0871</p>
                }
                <div className="mt-5">
                    <Button
                        onClick={() => {
                            navigate('/')
                        }}>
                        {t('Close the Window')}
                    </Button>
                </div>
            </div>
        </div>
    )
}
